import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rolesService, usersService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";
import ConfirmationModal from "./ConfirmationModal";
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";
import PasswordRequirements, { isPasswordValid, getPasswordError } from "./PasswordRequirements";

const formatDateTime = (value, locale = "de-DE") => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

function UsersTailwind() {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formValues, setFormValues] = useState({ username: "", password: "", confirmPassword: "", role: "scorekeeper", venue: "" });
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await rolesService.getRoles();
      if (!res.success) {
        throw new Error(res.error || "Fehler beim Laden der Rollen");
      }
      return res.data || [];
    }
  });

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await usersService.getUsers();
      if (!res.success) {
        throw new Error(res.error || "Fehler beim Laden der Benutzer");
      }
      return res.data || [];
    }
  });

  useEffect(() => {
    if (usersQuery.data) {
      setUsers(usersQuery.data);
    }
  }, [usersQuery.data]);

  useEffect(() => {
    if (editingUser) {
      setFormValues({
        username: editingUser.username,
        password: "",
        confirmPassword: "",
        role: editingUser.role,
        venue: editingUser.venue || ""
      });
    } else {
      setFormValues({
        username: "",
        password: "",
        confirmPassword: "",
        role: "scorekeeper",
        venue: ""
      });
    }
  }, [editingUser, modalOpen]);

  const availableRoles = rolesQuery.data || [];

  const defaultRoleName = useMemo(() => {
    if (!availableRoles.length) return "scorekeeper";
    const nonAdmin = availableRoles.find((role) => role.name !== "admin");
    return nonAdmin?.name || availableRoles[0].name;
  }, [availableRoles]);

  useEffect(() => {
    if (!editingUser && modalOpen) {
      setFormValues((prev) => ({
        ...prev,
        role: defaultRoleName
      }));
    }
  }, [defaultRoleName, editingUser, modalOpen]);

  useEffect(() => {
    if (selectedRole !== "all" && !availableRoles.some((role) => role.name === selectedRole)) {
      setSelectedRole("all");
    }
  }, [availableRoles, selectedRole]);

  const roleTranslations = useMemo(() => {
    const map = {};
    availableRoles.forEach((role) => {
      map[role.name] = t(`roles.${role.name}`, role.name);
    });
    return map;
  }, [availableRoles, t]);

  const roleFilterOptions = useMemo(() => {
    const options = [{ value: "all", label: t("admin.users.filters.all", "Alle") }];

    availableRoles.forEach((role) => {
      options.push({
        value: role.name,
        label: roleTranslations[role.name] || t(`roles.${role.name}`, role.name)
      });
    });

    return options;
  }, [availableRoles, roleTranslations, t]);

  const getRoleCardConfig = (roleName) => {
    switch (roleName) {
      case "admin":
        return { icon: "fas fa-user-shield", bg: "bg-emerald-100 dark:bg-emerald-900/30", iconColor: "text-emerald-600 dark:text-emerald-400" };
      case "scorekeeper":
        return { icon: "fas fa-clipboard-list", bg: "bg-blue-100 dark:bg-blue-900/30", iconColor: "text-blue-600 dark:text-blue-400" };
      case "caller":
        return { icon: "fas fa-bullhorn", bg: "bg-violet-100 dark:bg-violet-900/30", iconColor: "text-violet-600 dark:text-violet-400" };
      default:
        return { icon: "fas fa-user-tag", bg: "bg-slate-100 dark:bg-slate-700", iconColor: "text-slate-600 dark:text-slate-300" };
    }
  };

  const getRoleBadgeClasses = (roleName) => {
    switch (roleName) {
      case "admin":
        return "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-700/40";
      case "scorekeeper":
        return "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200/60 dark:border-blue-700/40";
      case "caller":
        return "bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200/60 dark:border-violet-700/40";
      default:
        return "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600";
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRoleFilterChange = (role) => {
    if (role !== "all" && !availableRoles.some((item) => item.name === role)) {
      setSelectedRole("all");
      return;
    }
    setSelectedRole(role);
  };

  const addToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: "info", message: "" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, type: "info", message: "" });
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === "all" || user.role === selectedRole;
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, selectedRole]);

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedRole !== "all") {
      const roleLabel = roleFilterOptions.find((r) => r.value === selectedRole)?.label || selectedRole;
      active.push({
        type: t("admin.users.filters.typeRole", "Rolle"),
        value: roleLabel,
        onRemove: () => setSelectedRole("all"),
      });
    }
    if (searchTerm.trim() !== "") {
      active.push({
        type: t("admin.users.filters.typeSearch", "Suche"),
        value: searchTerm,
        onRemove: () => setSearchTerm(""),
      });
    }
    return active;
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedRole("all");
  };

  // Pagination Logic
  const totalItems = filteredUsers.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedUsers = itemsPerPage === "all"
    ? filteredUsers
    : filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRole, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const roleCounts = useMemo(() => {
    const counts = {};
    users.forEach((user) => {
      counts[user.role] = (counts[user.role] || 0) + 1;
    });
    return counts;
  }, [users]);

  const usersCount = users.length;

  const createUserMutation = useMutation({
    mutationFn: usersService.createUser.bind(usersService),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setModalOpen(false);
      addToast("success", t("admin.users.toast.created", "Benutzer erfolgreich angelegt"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.createError", "Fehler beim Anlegen des Benutzers"));
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }) => usersService.updateUser(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["users"]);
      setModalOpen(false);
      addToast("success", t("admin.users.toast.updated", "Benutzer erfolgreich aktualisiert"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.updateError", "Fehler beim Aktualisieren des Benutzers"));
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: usersService.deleteUser.bind(usersService),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      addToast("success", t("admin.users.toast.deleted", "Benutzer erfolgreich gelöscht"));
      setDeleteModalOpen(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.deleteError", "Fehler beim Löschen des Benutzers"));
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }) => usersService.resetPassword(id, password),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["users"]);
      setResetModalOpen(false);
      setResetUser(null);
      setResetPassword("");
      setResetPasswordConfirm("");
      addToast("success", t("admin.users.toast.reset", "Passwort wurde zurückgesetzt"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.resetError", "Fehler beim Zurücksetzen des Passworts"));
    }
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormValues({ username: "", password: "", confirmPassword: "", role: "scorekeeper", venue: "" });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveUser = () => {
    if (!formValues.username || !formValues.username.trim()) {
      addToast("warning", t("admin.users.validation.usernameRequired", "Benutzername darf nicht leer sein."));
      return;
    }

    const roleIsValid =
      availableRoles.length === 0 ||
      availableRoles.some((role) => role.name === formValues.role);

    if (!roleIsValid) {
      addToast("warning", t("admin.users.validation.roleInvalid", "Bitte wähle eine gültige Rolle."));
      return;
    }

    if (editingUser) {
      const payload = {
        username: formValues.username.trim(),
        role: formValues.role
      };

      if (formValues.password) {
        const pwError = getPasswordError(formValues.password);
        if (pwError) {
          addToast("warning", pwError);
          return;
        }
        if (formValues.password !== formValues.confirmPassword) {
          addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
          return;
        }
        payload.password = formValues.password;
      }

      // Venue für Scorekeeper und Caller setzen
      if (formValues.role === "scorekeeper" || formValues.role === "caller") {
        payload.venue = formValues.venue || null;
      } else {
        payload.venue = null;
      }

      updateUserMutation.mutate({
        id: editingUser.id,
        payload
      });
    } else {
      if (!formValues.password || !isPasswordValid(formValues.password)) {
        addToast("warning", getPasswordError(formValues.password) || t("admin.users.validation.passwordMin", "Passwort erfüllt nicht die Anforderungen."));
        return;
      }
      if (formValues.password !== formValues.confirmPassword) {
        addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
        return;
      }
      const createPayload = {
        username: formValues.username.trim(),
        password: formValues.password,
        role: formValues.role
      };

      // Venue für Scorekeeper und Caller setzen
      if (formValues.role === "scorekeeper" || formValues.role === "caller") {
        createPayload.venue = formValues.venue || null;
      }

      createUserMutation.mutate(createPayload);
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const openResetModal = (user) => {
    setResetUser(user);
    setResetPassword("");
    setResetPasswordConfirm("");
    setResetModalOpen(true);
  };

  const handleResetPassword = () => {
    if (!resetPassword || !isPasswordValid(resetPassword)) {
      addToast("warning", getPasswordError(resetPassword) || t("admin.users.validation.passwordMin", "Passwort erfüllt nicht die Anforderungen."));
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
      return;
    }
    resetPasswordMutation.mutate({
      id: resetUser.id,
      password: resetPassword
    });
  };

  const closeResetModal = () => {
    if (resetPasswordMutation.isLoading) return;
    setResetModalOpen(false);
    setResetUser(null);
    setResetPassword("");
    setResetPasswordConfirm("");
  };

  if (usersQuery.isLoading || rolesQuery.isLoading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton - Right Aligned */}
          <div className="mb-6 flex items-center justify-end gap-2 animate-pulse">
            <div className="w-44 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6 animate-pulse">
            {/* Search Bar Skeleton */}
            <div className="mb-5">
              <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              </div>
            </div>
          </div>

          {/* KPI Skeleton — eine Zeile */}
          <div className="flex flex-nowrap gap-4 sm:gap-6 mb-6 overflow-x-auto animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex-1 basis-0 min-w-[11rem] shrink-0"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2" />
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(7)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Pagination Footer Skeleton */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12"></div>
                          <div className="h-9 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                          <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="flex items-center gap-2">
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            ))}
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (usersQuery.isError || rolesQuery.isError) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400" aria-hidden />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">
                {usersQuery.error?.message || rolesQuery.error?.message || t("admin.users.error.load", "Fehler beim Laden der Benutzer.")}
              </p>
              <button
                type="button"
                onClick={() => {
                  usersQuery.refetch();
                  rolesQuery.refetch();
                }}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary dark:bg-primary-light hover:bg-primary-dark dark:hover:bg-primary text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
              >
                <i className="fas fa-redo text-xs" aria-hidden />
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — wie Participants */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.users.title", "Benutzerverwaltung")}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                  <i className="fas fa-users text-[10px]" aria-hidden />
                  {filteredUsers.length}
                </span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 max-w-2xl leading-relaxed">
                {t("admin.users.subtitle", "Admins, Punktschreiber und weitere Rollen verwalten.")}
              </p>
            </div>
          </div>
        </div>

        {/* Primäre Aktion */}
        <div className="mb-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 h-9 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            <i className="fas fa-user-plus text-xs" aria-hidden />
            {t("admin.users.actions.add", "Benutzer hinzufügen")}
          </button>
        </div>

        {/* Filter — wie Participants */}
        <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
          <div className="mb-5">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t("admin.users.search.placeholder", "Benutzername suchen…")}
                className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
                autoComplete="off"
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs" aria-hidden />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.users.filters.title", "Rolle")}
              </label>
              <GenericSelectTailwind
                value={selectedRole}
                onChange={handleRoleFilterChange}
                options={roleFilterOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                placeholder={t("admin.users.filters.title", "Rolle")}
              />
            </div>
          </div>

          {getActiveFilters().length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-1">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  {t("admin.users.filters.activeHeading", "Aktive Filter")}
                </span>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                >
                  <i className="fas fa-undo text-[10px]" aria-hidden />
                  {t("admin.users.filters.reset", "Zurücksetzen")}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getActiveFilters().map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                  >
                    {filter.type}: {filter.value}
                    <button
                      type="button"
                      onClick={filter.onRemove}
                      className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
                      aria-label={t("admin.users.filters.reset", "Zurücksetzen")}
                    >
                      <i className="fas fa-times text-[9px]" aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* KPI-Karten — eine Zeile (auf schmalen Screens horizontal scrollbar) */}
        <div className="flex flex-nowrap gap-4 sm:gap-6 mb-6 min-w-0 overflow-x-auto pb-1 -mx-1 px-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 sm:p-6 flex items-center justify-between flex-1 basis-0 min-w-[11rem] sm:min-w-[10rem] shrink-0 sm:shrink">
            <div className="min-w-0 pr-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {t("admin.users.stats.total", "Gesamte Benutzer")}
              </p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">{usersCount}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
              <i className="fas fa-users text-base sm:text-lg" aria-hidden />
            </div>
          </div>
          {availableRoles.map((role) => {
            const config = getRoleCardConfig(role.name);
            return (
              <div
                key={role.id || role.name}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 sm:p-6 flex items-center justify-between flex-1 basis-0 min-w-[11rem] sm:min-w-[10rem] shrink-0 sm:shrink"
              >
                <div className="min-w-0 pr-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 line-clamp-2 leading-snug">
                    {roleTranslations[role.name] || t(`roles.${role.name}`, role.name)}
                  </p>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                    {roleCounts[role.name] || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${config.bg} ${config.iconColor} flex items-center justify-center shrink-0`}>
                  <i className={`${config.icon} text-base sm:text-lg`} aria-hidden />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabelle — Layout wie ParticipantsTailwind */}
        {filteredUsers.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.id", "ID")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.username", "Benutzername")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.role", "Rolle")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.venue", "Fläche")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.createdAt", "Erstellt am")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.updatedAt", "Aktualisiert am")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.users.table.actions", "Aktionen")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeClasses(user.role)}`}
                        >
                          {roleTranslations[user.role] || t(`roles.${user.role}`, user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.role === "scorekeeper" || user.role === "caller" ? (
                          user.venue ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                              {user.venue}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                              {t("admin.users.table.venueAll", "Beide")}
                            </span>
                          )
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                        {formatDateTime(user.createdAt, i18n.language)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                        {formatDateTime(user.updatedAt, i18n.language)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(user)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                            title={t("admin.users.actions.edit", "Bearbeiten")}
                          >
                            <i className="fas fa-edit text-[11px]" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => openResetModal(user)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors disabled:opacity-50"
                            title={t("admin.users.actions.resetPassword", "Passwort zurücksetzen")}
                            disabled={resetPasswordMutation.isLoading && resetUser?.id === user.id}
                          >
                            <i
                              className={`fas ${resetPasswordMutation.isLoading && resetUser?.id === user.id ? "fa-spinner fa-spin" : "fa-key"} text-[11px]`}
                              aria-hidden
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(user)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-700/50 transition-colors disabled:opacity-50"
                            title={t("admin.users.actions.delete", "Löschen")}
                            disabled={deleteUserMutation.isLoading}
                          >
                            <i className="fas fa-trash text-[11px]" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-700/30 border-t border-slate-200 dark:border-slate-700">
                  <tr>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {t("admin.users.pagination.show", "Zeige")}
                          </span>
                          <div
                            className="relative min-w-[4.5rem]"
                            style={{
                              zIndex: modalOpen || resetModalOpen || deleteModalOpen ? 1 : 100,
                              pointerEvents: modalOpen || resetModalOpen || deleteModalOpen ? "none" : "auto",
                            }}
                          >
                            <GenericSelectTailwind
                              value={itemsPerPage.toString()}
                              onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value, 10))}
                              options={[
                                { value: "10", label: "10" },
                                { value: "20", label: "20" },
                                { value: "50", label: "50" },
                                { value: "100", label: "100" },
                                { value: "all", label: t("admin.users.filters.all", "Alle") },
                              ]}
                              placeholder="10"
                              openUpward
                              whiteBackground
                              disabled={modalOpen || resetModalOpen || deleteModalOpen}
                            />
                          </div>
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {t("admin.users.pagination.perPage", "Einträge pro Seite")}
                          </span>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                          <span className="text-sm text-slate-600 dark:text-slate-400 tabular-nums">
                            {t("admin.users.pagination.range", "Zeige {{from}}–{{to}} von {{total}}", {
                              from: startIndex + 1,
                              to: Math.min(endIndex, totalItems),
                              total: totalItems,
                            })}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={scrollToTop}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                              title={t("admin.users.pagination.scrollTop", "Nach oben scrollen")}
                            >
                              <i className="fas fa-arrow-up text-xs" aria-hidden />
                            </button>
                            {itemsPerPage !== "all" && totalPages > 1 && (
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-left text-xs" aria-hidden />
                                </button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }
                                  return (
                                    <button
                                      key={pageNum}
                                      type="button"
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border text-sm font-medium transition-colors ${
                                        currentPage === pageNum
                                          ? "bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30"
                                          : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-right text-xs" aria-hidden />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredUsers.length === 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-4">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4 ring-1 ring-slate-200/80 dark:ring-slate-600/80">
              <i className="fas fa-user-slash text-slate-400 dark:text-slate-500 text-2xl" aria-hidden />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
              {t("admin.users.empty.title", "Keine Benutzer gefunden")}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || selectedRole !== "all"
                ? t("admin.users.empty.filter", "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen.")
                : t("admin.users.empty.action", "Fügen Sie den ersten Benutzer hinzu, um zu beginnen.")}
            </p>
            {!searchTerm && selectedRole === "all" && (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2.5 h-10 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                <i className="fas fa-user-plus text-xs" aria-hidden />
                {t("admin.users.actions.addFirst", "Ersten Benutzer hinzufügen")}
              </button>
            )}
          </div>
        )}

        {/* Modal */}
        <AdminModalTailwind
          isOpen={modalOpen}
          onClose={closeModal}
          title={
            editingUser
              ? t("admin.users.modal.editTitle", "Benutzer bearbeiten")
              : t("admin.users.modal.createTitle", "Benutzer hinzufügen")
          }
          subtitle={
            editingUser
              ? t("admin.users.modal.editSubtitle", "Passe Benutzername, Passwort oder Rolle an.")
              : t("admin.users.modal.createSubtitle", "Lege einen neuen Benutzer mit Rolle fest.")
          }
          onSave={handleSaveUser}
          onCancel={closeModal}
          saveButtonText={
            editingUser
              ? t("admin.users.modal.save", "Änderungen speichern")
              : t("admin.users.modal.create", "Benutzer erstellen")
          }
          cancelButtonText={t("admin.cancel", "Abbrechen")}
          isSaving={createUserMutation.isLoading || updateUserMutation.isLoading}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="username" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.form.username", "Benutzername")} <span className="text-red-500 normal-case">*</span>
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formValues.username}
                  onChange={handleFormChange}
                  placeholder={t("admin.users.form.usernamePlaceholder", "z.B. scorekeeper-01")}
                  className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.form.role", "Rolle")} <span className="text-red-500 normal-case">*</span>
                </label>
                <GenericSelectTailwind
                  value={formValues.role}
                  onChange={(value) => handleFormChange({ target: { name: "role", value } })}
                  options={availableRoles.length === 0
                    ? [{ value: "scorekeeper", label: t("roles.scorekeeper", "Punktschreiber") }]
                    : availableRoles.map((role) => ({
                      value: role.name,
                      label: roleTranslations[role.name] || t(`roles.${role.name}`, role.name)
                    }))}
                  placeholder="Rolle auswählen..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.form.password", "Passwort")}{" "}
                  {!editingUser && <span className="text-red-500 normal-case">*</span>}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formValues.password}
                  onChange={handleFormChange}
                  placeholder={
                    editingUser
                      ? t("admin.users.form.passwordPlaceholderOptional", "Leer lassen, um das Passwort unverändert zu lassen")
                      : t("admin.users.form.passwordPlaceholder", "Mindestens 12 Zeichen")
                  }
                  className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                  required={!editingUser}
                />
                <PasswordRequirements password={formValues.password} />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.form.confirmPassword", "Passwort bestätigen")}{" "}
                  {!editingUser && <span className="text-red-500 normal-case">*</span>}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formValues.confirmPassword}
                  onChange={handleFormChange}
                  placeholder={t("admin.users.form.confirmPasswordPlaceholder", "Passwort erneut eingeben")}
                  className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                  required={!editingUser}
                />
              </div>
            </div>

            {(formValues.role === "scorekeeper" || formValues.role === "caller") && (
              <div>
                <label htmlFor="venue" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.form.venue", "Zugewiesene Fläche")}
                </label>
                <GenericSelectTailwind
                  value={formValues.venue || ""}
                  onChange={(value) => handleFormChange({ target: { name: "venue", value: value || "" } })}
                  options={[
                    { value: "", label: t("admin.users.form.venueAll", "Beide Flächen (keine Zuweisung)") },
                    { value: "Fläche A", label: t("admin.users.form.venueA", "Fläche A") },
                    { value: "Fläche B", label: t("admin.users.form.venueB", "Fläche B") }
                  ]}
                  placeholder="Fläche auswählen..."
                />
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  {formValues.role === "scorekeeper"
                    ? t("admin.users.form.venueHint", "Wenn keine Fläche zugewiesen ist, kann der Scorekeeper sich eine Fläche auswählen.")
                    : t("admin.users.form.venueHintCaller", "Wenn keine Fläche zugewiesen ist, kann der Caller sich eine Fläche auswählen.")
                  }
                </p>
              </div>
            )}
          </div>
        </AdminModalTailwind>

        <AdminModalTailwind
          isOpen={resetModalOpen}
          onClose={closeResetModal}
          title={t("admin.users.reset.title", "Passwort zurücksetzen")}
          subtitle={
            resetUser
              ? t("admin.users.reset.subtitle", 'Neues Passwort für "{{username}}" festlegen.', {
                username: resetUser.username
              })
              : ""
          }
          onSave={handleResetPassword}
          onCancel={closeResetModal}
          saveButtonText={t("admin.users.reset.save", "Passwort setzen")}
          cancelButtonText={t("admin.cancel", "Abbrechen")}
          isSaving={resetPasswordMutation.isLoading}
        >
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="reset-password" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.reset.newPassword", "Neues Passwort")} <span className="text-red-500 normal-case">*</span>
                </label>
                <input
                  id="reset-password"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder={t("admin.users.form.passwordPlaceholder", "Mindestens 12 Zeichen")}
                  className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                  required
                />
                <PasswordRequirements password={resetPassword} />
              </div>

              <div>
                <label htmlFor="reset-password-confirm" className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.users.reset.confirmPassword", "Passwort bestätigen")}{" "}
                  <span className="text-red-500 normal-case">*</span>
                </label>
                <input
                  id="reset-password-confirm"
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                  placeholder={t("admin.users.form.confirmPasswordPlaceholder", "Passwort erneut eingeben")}
                  className="w-full h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm"
                  required
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary-light/15 text-primary dark:text-primary-light flex items-center justify-center shrink-0 ring-1 ring-primary/10 dark:ring-primary-light/20">
                <i className="fas fa-info-circle text-xs" aria-hidden />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  {t("admin.users.resetInfoTitle", "Hinweis")}
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  {t("admin.users.reset.hint", "Das neue Passwort wird unmittelbar aktiv. Teile es dem Nutzer sicher mit.")}
                </p>
              </div>
            </div>
          </div>
        </AdminModalTailwind>

        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => {
            if (deleteUserMutation.isLoading) return;
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={() => {
            if (userToDelete && !deleteUserMutation.isLoading) {
              deleteUserMutation.mutate(userToDelete.id);
            }
          }}
          title={t("admin.users.delete.title", "Benutzer löschen")}
          subtitle={t("admin.users.delete.warning", "Diese Aktion kann nicht rückgängig gemacht werden.")}
          message={
            userToDelete
              ? t("admin.users.delete.subtitle", 'Soll der Benutzer "{{username}}" wirklich gelöscht werden? Der Benutzer verliert sofort den Zugang zum System.', {
                username: userToDelete.username
              })
              : ""
          }
          confirmText={t("admin.users.delete.confirm", "Löschen")}
          cancelText={t("admin.cancel", "Abbrechen")}
          type="danger"
          layout="inline"
          isLoading={deleteUserMutation.isLoading}
        />

        <Toast message={toast.message} type={toast.type} isVisible={toast.show} onClose={hideToast} />
      </div>
    </main>
  );
}

export default UsersTailwind;

